import React, { useEffect, useState } from 'react';
import { AlertTriangle, CheckCircle, Clock, Download, Loader2 } from 'lucide-react';
import { apiService } from '../services/apiService';
import { trackEvent } from '../services/telemetry';
import { formatCurrency } from '../utils/formatting';

// The order confirmation is read from the order in A+, so it can be downloaded only once the
// order shows up there, usually a minute or two after submit (Tonya, 2026-10-23: "only after
// avail" — the customer has to see whether they're billed tax). Polls until then.
const POLL_MS = 30_000;
const GIVE_UP_MS = 30 * 60 * 1000;

export default function OrderConfirmationButton({ correlationId, orderNumber, compact = false }) {
  const [status, setStatus] = useState({ ready: false, missing: [] });
  const [gaveUp, setGaveUp] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!correlationId) return undefined;
    let cancelled = false;
    let timer = null;
    const started = Date.now();
    const poll = async () => {
      try {
        const result = await apiService.checkOrderConfirmation(correlationId, orderNumber);
        if (cancelled) return;
        setStatus(result);
        setError(null);
        if (result.ready) {
          if (result.missing?.length) {
            trackEvent('order_lines_missing_in_aplus', { correlationId, orderNumber, count: result.missing.length });
          }
          return;
        }
        if (result.conflict) {
          trackEvent('order_confirmation_number_conflict', { correlationId, orderNumber });
          return;
        }
      } catch (err) {
        if (!cancelled) setError(err.message);
      }
      if (cancelled) return;
      if (Date.now() - started > GIVE_UP_MS) {
        setGaveUp(true);
        return;
      }
      timer = setTimeout(poll, POLL_MS);
    };
    poll();
    return () => { cancelled = true; clearTimeout(timer); };
  }, [correlationId, orderNumber]);

  const download = async () => {
    setDownloading(true);
    setError(null);
    try {
      await apiService.downloadOrderConfirmation(correlationId, orderNumber);
      trackEvent('order_confirmation_downloaded', { correlationId, orderNumber, from: 'submitted' });
    } catch (err) {
      setError(err.message);
    } finally {
      setDownloading(false);
    }
  };

  const missing = status.ready ? status.missing || [] : [];
  const size = compact ? 'text-xs px-2 py-1' : 'px-6 py-3';
  const icon = compact ? 'w-3 h-3' : 'w-4 h-4';

  return (
    <div className={compact ? 'flex flex-col items-end gap-1' : 'flex flex-col items-center gap-2'}>
      {status.ready ? (
        <button
          onClick={download}
          disabled={downloading}
          className={`${size} bg-emerald-600 hover:bg-emerald-500 text-white rounded-lg transition-colors flex items-center gap-2 disabled:opacity-50`}
        >
          {downloading ? <Loader2 className={`${icon} animate-spin`} /> : <Download className={icon} />}
          {compact ? 'Confirmation' : 'Download Order Confirmation'}
        </button>
      ) : status.conflict ? (
        <p className={`text-amber-400 flex items-start gap-1 text-left ${compact ? 'text-xs max-w-xs' : 'text-sm max-w-md'}`}>
          <AlertTriangle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
          <span>{status.message}</span>
        </p>
      ) : (
        <span className={`${size} rounded-lg bg-slate-700/60 text-slate-300 flex items-center gap-2`}>
          {gaveUp ? <AlertTriangle className={`${icon} text-amber-400`} /> : <Clock className={`${icon} animate-pulse`} />}
          {gaveUp
            ? 'Not in A+ yet. Check with support.'
            : compact ? 'Waiting for A+…' : 'Confirmation available once the order is in A+…'}
        </span>
      )}
      {/* OP's total vs what landed in A+, item lines only (Tonya, 2026-10-28): proof it transmitted. */}
      {/* The order's A+ hold, e.g. CR credit / GM gross margin / SP slow pay (Tonya, 2026-10-29). */}
      {status.ready && status.hold && (
        <p className={`text-amber-400 flex items-start gap-1 text-left ${compact ? 'text-xs max-w-xs' : 'text-sm max-w-md'}`}>
          <AlertTriangle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
          <span>{`On hold in A+: ${status.hold.code}${status.hold.description ? ` – ${status.hold.description}` : ''}`}</span>
        </p>
      )}
      {status.ready && status.transmission && (
        <p className={`flex items-start gap-1 text-left ${status.transmission.ok ? 'text-emerald-400' : 'text-amber-400'} ${compact ? 'text-xs max-w-xs' : 'text-sm max-w-md'}`}>
          {status.transmission.ok
            ? <CheckCircle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
            : <AlertTriangle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />}
          <span>
            OP sent {formatCurrency(status.transmission.opTotal)}, A+ has {formatCurrency(status.transmission.aplusTotal)}
            {status.transmission.ok
              ? '. Transmitted correctly.'
              : `. Difference ${formatCurrency(status.transmission.difference)}.`}
            {' '}<span className="text-slate-500">(items only; A+ adds charges and tax)</span>
          </span>
        </p>
      )}
      {missing.length > 0 && (
        <p className={`text-amber-400 flex items-start gap-1 text-left ${compact ? 'text-xs max-w-xs' : 'text-sm max-w-md'}`}>
          <AlertTriangle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
          <span>
            Not on the A+ order:{' '}
            {missing.map(m => `line ${m.lineNumber ?? '?'} (${m.itemNumber})`).join(', ')}.
            Fix it in A+ before sending the confirmation.
          </span>
        </p>
      )}
      {status.ready && status.verified === false && (
        <p className="text-xs text-slate-500">Test order: not verified against A+.</p>
      )}
      {error && <p className="text-xs text-red-400">{error}</p>}
    </div>
  );
}
